import {noop} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {
  generateCrossModelComparisonTables,
  isCrossModelValidationVerified,
} from 'notebook/ai-tools/flexible-data-entry/CrossModelValidation';
import {FillStructuredTableUsingAIResponse} from 'notebook/ai-tools/NotebookAIToolsDAO';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {listToReadable} from 'util/NameUtil';

interface FlexibleDataEntryTablePreviewProps {
  structuredTable: StructuredTable<unknown, unknown>;
  fillResult: FillStructuredTableUsingAIResponse;
}

export default function FlexibleDataEntryTablePreview({
  structuredTable,
  fillResult,
}: FlexibleDataEntryTablePreviewProps): JSX.Element | null {
  const [shouldHideMatchingRows, setShouldHideMatchingRows] = useState<boolean>(false);

  if (!assertAndContinue(fillResult.runResults.length > 0, 'Unexpected no run results.')) {
    return <div>No rows were generated.</div>;
  }
  const {primaryTable, secondaryExtraTables} = generateCrossModelComparisonTables(
    fillResult,
    shouldHideMatchingRows
  );
  const isVerified = isCrossModelValidationVerified(fillResult);
  const modelsDescription = listToReadable(fillResult.runResults.map((result) => result.displayName));
  return (
    <div className="mediocre">
      {isVerified ? (
        <InlineBanner
          bannerType={InlineBannerType.SUCCESS}
          text={<span>Verification passed: AI models {modelsDescription} produced the same result.</span>}
        />
      ) : (
        <div>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={
              <span>
                Verification failed: AI models {modelsDescription} produced different results, so the
                resulting data may contain inaccuracies. Carefully review the data below and click "Approve
                and insert rows" to approve the results from {fillResult.runResults[0].displayName}.
              </span>
            }
          />
          <div className={css(styles.suggestions)}>
            Suggestions to resolve discrepancies:
            <ul>
              <li>
                Retry the operation with additional AI instructions to clarify any ambiguities in the data
                entry task.
              </li>
              <li>
                If the AI system is performing a multi-step lookup process, directly add custom columns
                corresponding to intermediate steps.
              </li>
              <li>Retry the operation; results may vary slightly from run to run.</li>
            </ul>
          </div>
          <CheckboxInput
            id="hide-matching-rows"
            checked={shouldHideMatchingRows}
            onChange={(e) => {
              setShouldHideMatchingRows(e.target.checked);
            }}
            label="Only show mismatch rows"
          />
        </div>
      )}
      <SimpleTableEditable
        {...standaloneProps}
        locked={true}
        cid="tmp-flexible-data-entry-table-preview"
        table={{
          name: structuredTable.name,
          columnLabels: structuredTable.columns.map((c) => c.name),
          table: primaryTable,
          widths: structuredTable.columns.map((c) => c.width),
          columnConstraints: structuredTable.columns.map((c) => null),
        }}
        hideFormulaBox={true}
        enableFormulas={false}
        storeState={noop}
      />
      {secondaryExtraTables.map(({displayName, table}, i) => (
        <div key={displayName}>
          <div>Model {displayName} produced extra rows that will not be included:</div>
          <SimpleTableEditable
            {...standaloneProps}
            locked={true}
            cid={`tmp-flexible-data-entry-table-preview-secondary-${i}`}
            table={{
              name: `Extra rows from ${displayName}`,
              columnLabels: structuredTable.columns.map((c) => c.name),
              table,
              widths: structuredTable.columns.map((c) => c.width),
              columnConstraints: structuredTable.columns.map((c) => null),
            }}
            hideFormulaBox={true}
            enableFormulas={false}
            storeState={noop}
          />
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  suggestions: {
    marginTop: 8,
  },
});
